import type { Ref } from 'vue';
import omit from 'lodash/omit';
import { computed, unref, ref } from 'vue';
import type { ParentChildMap } from '@/shared/legacy/lib/hierarchy/hierarchy-map';
import type { TreeId } from '@/shared/legacy/api/dictionary-partial/types';
import { TREE_ROOT_ID } from '@/shared/legacy/api/dictionary-partial/types';
import { isLabelField, deepField, keyField } from './constants';

export type Item = {
  id: number | string;
  active?: boolean;
  attributes?: {
    extraDeep?: number;
  };
  items?: Item[];
  [deepField]?: number;
  [keyField]?: string;
};

type ExtendsItem = Omit<Item, 'items'> & {
  [keyField]: string;
  [isLabelField]?: boolean;
  [deepField]?: number;
};

function getItems(
  items: Item[],
  params: {
    showInactive: boolean;
    parentChildMap: ParentChildMap;
  }
): ExtendsItem[] {
  const { showInactive, parentChildMap } = params;
  const result: ExtendsItem[] = [];
  items.forEach((item) => {
    const active = item.active !== false;
    if (!active && !showInactive) {
      return;
    }

    if (Array.isArray(item.items)) {
      if (item.items.length === 0) {
        return;
      }
      result.push(
        Object.freeze({
          ...omit(item, 'items'),
          active,
          [keyField]: item[keyField] ?? `group-${item.id}`,
          [isLabelField]: true
        }),
        ...getItems(item.items, params)
      );
    } else {
      result.push(
        Object.freeze({
          ...item,
          [deepField]: item.hasOwnProperty(deepField)
            ? item[deepField]
            : getItemDeep(item.id, parentChildMap, item.attributes?.extraDeep),
          active,
          [keyField]: item[keyField] ?? `item-${item.id}`
        })
      );
    }
  });

  return result;
}

export function getItemDeep(id: TreeId, parentChildMap: ParentChildMap, extra = 0): number {
  let deep = -1;
  let idString = String(id);
  while (idString in parentChildMap) {
    deep++;
    idString = String(parentChildMap[idString] || TREE_ROOT_ID);
  }
  return deep + extra;
}

export type Options<I extends Array<unknown>> = {
  showInactive?: Ref<boolean>;
  parentChildMap: Ref<ParentChildMap>;
  postprocessing?: Ref<(items: I) => I>;
};

export function useFlatList<I extends Ref<Item[]>>(
  items: I,
  {
    showInactive = ref(false),
    parentChildMap,
    postprocessing = ref((items: I['value']) => items)
  }: Options<I['value']>
) {
  const resultItems = computed(() => {
    return unref(postprocessing)(
      getItems(unref(items), {
        showInactive: unref(showInactive),
        parentChildMap: unref(parentChildMap)
      })
    );
  });

  return {
    items: resultItems
  };
}
